import "./styles.scss";
import React, { useContext, useRef } from "react";
import { Controller, FieldPath } from "react-hook-form";
import { Checkbox, Dropdown, Form, Popup } from "semantic-ui-react";
import { PlayerCardWidgetOwner } from "../../../../../../services/bedrock";
import PlaceholderButton from "../../../PlayerCardTab/Components/Fields/PlaceholderButton";
import _ from "lodash";
import { PlayerCardFieldBase } from "./PlayerCardFieldBase";
import { PlayerCardSettingsContext } from "../../../../Contexts";
import RecordSelector from "../../../../RecordSelector";
import ReviewSelector from "../../../../ReviewSelector";
import ReviewSourcesSelect from "../../../../ReviewSourcesSelect";

type PlayerCardFieldProps = {
  index: number;
  fieldName: FieldPath<PlayerCardWidgetOwner>;
  component: React.ElementType;
  fieldLabel: JSX.Element | string;
  rules?: any;
  [key: string]: any;
};

const PlayerCardField: React.FC<PlayerCardFieldProps> = ({
  index,
  fieldName,
  component: Component,
  fieldLabel,
  rules,
  ...rest
}) => {
  const {
    control,
    isTemplate,
    templateValue,
    fieldValue,
    getErrorComponent,
    getTemplateLockButton,
    getLockedTemplateAlert,
    useDefaultValueButton,
  } = PlayerCardFieldBase({ index, fieldName, fieldLabel });
  const { Placeholders, User } = useContext(PlayerCardSettingsContext);
  const inputRef = useRef<HTMLInputElement>(null);

  const hasDefault = templateValue !== undefined && templateValue !== null && templateValue !== "";
  const isNotSetToDefault = !isTemplate && hasDefault && templateValue !== fieldValue;

  const useDefaultValue = () => {
    if (!hasDefault) return;

    var element = inputRef.current;
    // Semantic UI's components don't assign the inputRef to the actual input element, so we need to find it ourselves
    while (element && !(element instanceof HTMLElement)) {
      element = (element as any)?.inputRef?.current;
    }
    if (!element) return;
    element.focus();
    element.setSelectionRange(0, element.value.length);
    // Setting the field value programmatically this way (as opposed to using react-hook-form's `setValue` method) makes it so the user can undo/redo the changes.
    document.execCommand("insertText", false, templateValue);
  };

  const displayFieldLabelInline = Component === Checkbox;

  return (
    <div className="playerCardField">
      {displayFieldLabelInline || (
        <div className="playerCardFieldLabel">
          {getTemplateLockButton()}
          {fieldLabel}
          {isNotSetToDefault && (
            <>
              <div className="grow" />
              {getLockedTemplateAlert()}
              {useDefaultValueButton(useDefaultValue)}
            </>
          )}
        </div>
      )}
      <Controller
        name={`widgets.${index}.owner.${fieldName}`}
        control={control}
        rules={rules}
        render={({ field, fieldState: { error } }) => {
          const effectiveFieldProps: any = { ...field, ...rest };
          const isRecordSelector = (
            [RecordSelector, ReviewSelector, ReviewSourcesSelect] as React.ElementType[]
          ).includes(Component);
          const isTextInput = Component !== Dropdown && !isRecordSelector && Component !== Checkbox;
          if (isTextInput && effectiveFieldProps.value == null) effectiveFieldProps.value = "";
          let shouldDisplayPlaceholders: boolean;
          if (Component === Dropdown) {
            effectiveFieldProps.onChange = (_e: any, data: any) => field.onChange(data.value);
            shouldDisplayPlaceholders = false;
            effectiveFieldProps.disabled = !User.hasWritePermission;
            effectiveFieldProps.ref = inputRef;
          } else if (isRecordSelector) {
            shouldDisplayPlaceholders = false;
            delete effectiveFieldProps.ref;
          } else {
            shouldDisplayPlaceholders =
              Placeholders.displayPlaceholders &&
              Placeholders.placeholders &&
              Object.keys(Placeholders.placeholders).length > 0;
            effectiveFieldProps.readOnly = !User.hasWritePermission;
            effectiveFieldProps.ref = inputRef;
          }
          if (rest.type === "number") {
            effectiveFieldProps.onChange = (e: React.ChangeEvent<HTMLInputElement>) =>
              field.onChange(e.target.value === "" ? null : Number(e.target.value));
            shouldDisplayPlaceholders = false;
          }
          if (Component === Checkbox) {
            effectiveFieldProps.onChange = (_e: any, data: any) => field.onChange(data.checked);
            delete effectiveFieldProps.value;
            effectiveFieldProps.checked = field.value;
            shouldDisplayPlaceholders = false;
          }
          if (displayFieldLabelInline) {
            effectiveFieldProps.label = <label className="playerCardFieldLabel">{fieldLabel}</label>;
          }
          return (
            <Form.Field error={!!error}>
              <div className="playerCardComponentContainer">
                {displayFieldLabelInline && getTemplateLockButton()}
                <Popup
                  disabled={!shouldDisplayPlaceholders || !User.hasWritePermission}
                  on="focus"
                  position="right center"
                  content={
                    <div className="templateFieldPlaceholders">
                      {Object.keys(Placeholders.placeholders || {}).map(key => (
                        <div key={key}>
                          <div className="templateFieldPlaceholdersGroupLabel">{_.startCase(key)} Placeholders</div>
                          <div className="templateFieldPlaceholdersGroup">
                            {Object.entries(Placeholders.placeholders?.[key] || []).map(
                              ([displayName, placeholder]) => (
                                <PlaceholderButton
                                  key={`placeholder-${placeholder}`}
                                  placeholder={placeholder}
                                  displayName={displayName}
                                />
                              )
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  }
                  trigger={<Component {...effectiveFieldProps} />}
                />
                {displayFieldLabelInline && isNotSetToDefault && (
                  <>
                    {getLockedTemplateAlert()}
                    {useDefaultValueButton(() => field.onChange(templateValue))}
                  </>
                )}
              </div>
              {getErrorComponent(error)}
            </Form.Field>
          );
        }}
      />
    </div>
  );
};
export default PlayerCardField;
